import {
	INFRACTION_API_STATUS,
	INFRACTION_DISPLAY_STATUS,
} from "./infraction-api-enums.constant";

export type InfractionStatusBannerVariant =
	| "neutral"
	| "warning"
	| "success"
	| "error"
	| "orange"
	| "brand"
	| "indigo";

export type InfractionStatusBannerIcon =
	| "hourglass"
	| "check-circle"
	| "x-circle"
	| "alert-circle"
	| "clock"
	| "refresh"
	| "info";

export type InfractionStatusBannerLayout = "circular" | "square";

export type InfractionStatusBannerConfig = {
	description: string;
	variant: InfractionStatusBannerVariant;
	icon: InfractionStatusBannerIcon;
	layout: InfractionStatusBannerLayout;
};

export type InfractionStatusBannerPalette = {
	root: string;
	title: string;
	description: string;
	circularIconWrapper: string;
	circularIcon: string;
	squareIconWrapper: string;
	squareIcon: string;
};

export const INFRACTION_STATUS_BANNER_PALETTES: Record<
	InfractionStatusBannerVariant,
	InfractionStatusBannerPalette
> = {
	neutral: {
		root: "border-border-primary bg-bg-secondary",
		title: "text-fg-primary",
		description: "text-fg-secondary",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-fg-quaternary",
		squareIconWrapper: "bg-utility-gray-500",
		squareIcon: "text-fg-white",
	},
	warning: {
		root: "border-utility-warning-200 bg-utility-warning-50",
		title: "text-utility-warning-700",
		description: "text-utility-warning-700",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-utility-warning-500",
		squareIconWrapper: "bg-utility-warning-500",
		squareIcon: "text-fg-white",
	},
	success: {
		root: "border-fg-success-primary bg-bg-success-primary",
		title: "text-success-primary",
		description: "text-success-primary",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-fg-success-primary",
		squareIconWrapper: "bg-fg-success-primary",
		squareIcon: "text-fg-white",
	},
	error: {
		root: "border-border-error bg-bg-error-primary",
		title: "text-error-primary",
		description: "text-error-primary",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-fg-error-primary",
		squareIconWrapper: "bg-fg-error-primary",
		squareIcon: "text-fg-white",
	},
	orange: {
		root: "border-utility-orange-200 bg-utility-orange-50",
		title: "text-utility-orange-700",
		description: "text-utility-orange-700",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-utility-orange-500",
		squareIconWrapper: "bg-utility-orange-700",
		squareIcon: "text-fg-white",
	},
	brand: {
		root: "border-border-brand bg-bg-brand-primary",
		title: "text-fg-brand-primary",
		description: "text-fg-brand-primary",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-fg-brand-secondary",
		squareIconWrapper: "bg-fg-brand-secondary",
		squareIcon: "text-fg-white",
	},
	indigo: {
		root: "border-utility-indigo-200 bg-utility-indigo-50",
		title: "text-utility-indigo-700",
		description: "text-utility-indigo-700",
		circularIconWrapper: "bg-bg-primary",
		circularIcon: "text-utility-indigo-500",
		squareIconWrapper: "bg-utility-indigo-500",
		squareIcon: "text-fg-white",
	},
};

const displayStatusBannerConfig: Record<number, InfractionStatusBannerConfig> =
	{
		[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED]: {
			description:
				"Essa infração aguarda o processamento da notificação recebida",
			variant: "neutral",
			icon: "info",
			layout: "circular",
		},
		[INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION]: {
			description:
				"Essa infração está aguardando identificação do condutor responsável",
			variant: "warning",
			icon: "hourglass",
			layout: "circular",
		},
		[INFRACTION_DISPLAY_STATUS.DRIVER_INDICATION_EXPIRED]: {
			description:
				"O prazo para identificação do condutor expirou. Risco de multa NIC.",
			variant: "error",
			icon: "alert-circle",
			layout: "square",
		},
		[INFRACTION_DISPLAY_STATUS.PENDING_PAYMENT]: {
			description: "Essa infração está aguardando pagamento",
			variant: "warning",
			icon: "hourglass",
			layout: "circular",
		},
		[INFRACTION_DISPLAY_STATUS.PAYMENT_EXPIRED]: {
			description: "O prazo para pagamento desta infração expirou",
			variant: "error",
			icon: "alert-circle",
			layout: "square",
		},
		[INFRACTION_DISPLAY_STATUS.DRIVER_ASSIGNED]: {
			description: "Essa infração já possui condutor indicado",
			variant: "success",
			icon: "check-circle",
			layout: "circular",
		},
		[INFRACTION_DISPLAY_STATUS.PAID]: {
			description: "Esta infração foi registrada como paga",
			variant: "indigo",
			icon: "check-circle",
			layout: "circular",
		},
		[INFRACTION_DISPLAY_STATUS.CANCELLED]: {
			description: "Esta infração foi registrada como cancelada",
			variant: "orange",
			icon: "x-circle",
			layout: "circular",
		},
	};

const apiStatusBannerConfig: Record<number, InfractionStatusBannerConfig> = {
	[INFRACTION_API_STATUS.NOTIFICATION_RECEIVED]:
		displayStatusBannerConfig[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED],
	[INFRACTION_API_STATUS.AWAITING_DRIVER_INDICATION]:
		displayStatusBannerConfig[
			INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION
		],
	[INFRACTION_API_STATUS.DRIVER_INDICATED]:
		displayStatusBannerConfig[INFRACTION_DISPLAY_STATUS.DRIVER_ASSIGNED],
	[INFRACTION_API_STATUS.IN_DEFENSE]: {
		description: "Esta infração está em processo de defesa",
		variant: "brand",
		icon: "refresh",
		layout: "square",
	},
	[INFRACTION_API_STATUS.PENDING_PAYMENT]:
		displayStatusBannerConfig[INFRACTION_DISPLAY_STATUS.PENDING_PAYMENT],
	[INFRACTION_API_STATUS.PAID]:
		displayStatusBannerConfig[INFRACTION_DISPLAY_STATUS.PAID],
	[INFRACTION_API_STATUS.EXPIRED]:
		displayStatusBannerConfig[
			INFRACTION_DISPLAY_STATUS.DRIVER_INDICATION_EXPIRED
		],
	[INFRACTION_API_STATUS.CANCELLED]:
		displayStatusBannerConfig[INFRACTION_DISPLAY_STATUS.CANCELLED],
	[INFRACTION_API_STATUS.WARNING]: {
		description: "Esta infração foi registrada como advertência",
		variant: "warning",
		icon: "alert-circle",
		layout: "circular",
	},
};

const defaultStatusBannerConfig: InfractionStatusBannerConfig = {
	description: "Atualize o status da infração conforme necessário",
	variant: "neutral",
	icon: "info",
	layout: "circular",
};

export type ResolvedInfractionStatusBanner = InfractionStatusBannerConfig & {
	title: string;
	palette: InfractionStatusBannerPalette;
};

export function resolveInfractionStatusBanner(
	displayStatus: number,
	statusCode: number,
	statusLabel: string
): ResolvedInfractionStatusBanner {
	const config =
		displayStatusBannerConfig[displayStatus] ??
		apiStatusBannerConfig[statusCode] ??
		defaultStatusBannerConfig;

	return {
		...config,
		title: statusLabel.trim() || "Status da infração",
		palette: INFRACTION_STATUS_BANNER_PALETTES[config.variant],
	};
}
